/**
 * Zone materials. All use view-space distance only (modelViewMatrix /
 * projectionMatrix), so they behave identically with three's XR sub-cameras
 * and with the plain PerspectiveCamera.
 */
import * as THREE from 'three';
import { drawOsmIcon, osmIcon, OSM_TRANSPORT_ICON_COLOR } from '../assets/osmIcons';
import { RENDER_STYLE } from './style';

export interface SharedUniforms {
  /** Global overlay opacity (overlay alpha x settings.overlayOpacity). */
  uGlobal: THREE.IUniform<number>;
  /**
   * Distance fade shared by every overlay layer: x = near (full opacity), y = far (0), both
   * driven each frame from settings.viewDistanceM (view-space distance, metres).
   */
  uFade: THREE.IUniform<THREE.Vector2>;
  /**
   * User position in WorldENU (x, z), updated each frame in ZoneScene.apply(). Used by the
   * shore occluder to suppress its height near the user (GPS error tolerance).
   */
  uUser: THREE.IUniform<THREE.Vector2>;
}

const ZONE_VERT = /* glsl */ `
varying vec3 vViewPos;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vViewPos = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

const ZONE_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
uniform float uGlobal;
uniform vec2 uFade;
varying vec3 vViewPos;
void main() {
  // Per-fragment distance: zone triangles can be kilometres long.
  float fade = 1.0 - smoothstep(uFade.x, uFade.y, length(vViewPos));
  gl_FragColor = vec4(uColor, uOpacity * uGlobal * fade);
  #include <colorspace_fragment>
}
`;

/**
 * Finish line: a vertical band standing on the water. Bottom vertices stay at
 * y = 0, top vertices are lifted by h = clamp(d * tan(angle), hMin, hMax) where
 * d is the view distance of the column's base, so the band keeps a roughly
 * constant angular height.
 */
const FINISH_VERT = /* glsl */ `
attribute float aSide;
attribute float aAlong;
uniform vec3 uHeight; // x = tan(angular height), y = min, z = max (m)
varying vec3 vViewPos;
varying float vH;
varying float vY;
varying float vS;
void main() {
  vec4 base = modelViewMatrix * vec4(position.x, 0.0, position.z, 1.0);
  float h = clamp(length(base.xyz) * uHeight.x, uHeight.y, uHeight.z);
  float y = aSide * h;
  // Column 1 of the model-view matrix is the object's up axis in view space.
  vec4 mv = base + vec4(modelViewMatrix[1].xyz * y, 0.0);
  vViewPos = mv.xyz;
  vH = h;
  vY = y;
  vS = aAlong;
  gl_Position = projectionMatrix * mv;
}
`;

const FINISH_FRAG = /* glsl */ `
uniform vec3 uWhite;
uniform vec3 uBlack;
uniform float uOpacity;
uniform float uGlobal;
uniform vec2 uFade;
varying vec3 vViewPos;
varying float vH;
varying float vY;
varying float vS;

// Box-filtered checkerboard (period 2 in both axes), anti-aliased via derivatives.
float checker(vec2 p) {
  vec2 w = fwidth(p) + 1e-3;
  vec2 i = 2.0 * (abs(fract((p - 0.5 * w) * 0.5) - 0.5) - abs(fract((p + 0.5 * w) * 0.5) - 0.5)) / w;
  return 0.5 - 0.5 * i.x * i.y;
}

void main() {
  // Two rows; square width is the next power of two >= the row height (aspect
  // 1..2), so squares stay roughly square, never collapse into thin slivers,
  // and do not slide along the line as the camera moves.
  float rowH = 0.5 * vH;
  float w = exp2(ceil(log2(rowH) - 0.01));
  float c = checker(vec2(vS / w, vY / rowH));
  float fade = 1.0 - smoothstep(uFade.x, uFade.y, length(vViewPos));
  gl_FragColor = vec4(mix(uBlack, uWhite, c), uOpacity * uGlobal * fade);
  #include <colorspace_fragment>
}
`;

/**
 * Slipway markers: a thin pole topped by a square sign, standing on the water, height scaled
 * like the finish band (angular height clamped to a min/max). The pole stays a flat vertical
 * quad (its horizontal extent is negligible), but the sign must face the camera so its OSM icon
 * is never seen edge-on: this is a cylindrical billboard — vertical offsets follow view-space
 * "up" (modelViewMatrix's column 1, like the pole/finish band already did), horizontal offsets
 * follow view-space "right", derived as the axis perpendicular to both "up" and the view
 * direction from the camera to the marker's base. Offsets are expressed as fractions of the
 * per-marker height `h` (see uHeight) so the sign stays square and the pole doesn't thin out
 * relatively at distance. Colour: fill/outline baked in by the geometry builder (aColor) for the
 * pole/outline/plain-sign quads, or the OSM carto icon texture (uIcon) for the textured sign
 * quad — selected via aUv (see SLIPWAY_FRAG).
 */
const SLIPWAY_VERT = /* glsl */ `
attribute vec2 aOffset; // x = horizontal offset, y = height fraction (aFrac before); frac of h
attribute vec2 aUv;
attribute vec3 aColor;
uniform vec3 uHeight; // x = tan(angular height), y = min, z = max (m)
varying vec3 vViewPos;
varying vec3 vColor;
varying vec2 vUv;
void main() {
  vec4 base = modelViewMatrix * vec4(position, 1.0);
  float h = clamp(length(base.xyz) * uHeight.x, uHeight.y, uHeight.z);
  vec3 upV = modelViewMatrix[1].xyz;
  // View direction from the camera to the marker's base (degenerate only if the camera sits
  // exactly on the marker, in which case any horizontal axis is as good as any other).
  float d = length(base.xyz);
  vec3 viewDir = d > 1e-5 ? base.xyz / d : vec3(0.0, 0.0, -1.0);
  vec3 rightV = cross(upV, -viewDir);
  float rl = length(rightV);
  rightV = rl > 1e-5 ? rightV / rl : vec3(1.0, 0.0, 0.0);
  vec4 mv = base + vec4((upV * aOffset.y + rightV * aOffset.x) * h, 0.0);
  vViewPos = mv.xyz;
  vColor = aColor;
  vUv = aUv;
  gl_Position = projectionMatrix * mv;
}
`;

const SLIPWAY_FRAG = /* glsl */ `
uniform float uGlobal;
uniform vec2 uFade;
uniform sampler2D uIcon;
varying vec3 vViewPos;
varying vec3 vColor;
varying vec2 vUv;
void main() {
  vec4 color = vUv.x < 0.0 ? vec4(vColor, 1.0) : texture2D(uIcon, vUv);
  float fade = 1.0 - smoothstep(uFade.x, uFade.y, length(vViewPos));
  gl_FragColor = vec4(color.rgb, color.a * uGlobal * fade);
  #include <colorspace_fragment>
}
`;

function baseParams(): THREE.ShaderMaterialParameters {
  return {
    transparent: true,
    // depthTest: the shore occluder (RENDER_ORDER.occluder, painted first) writes depth
    // along the lakeOutline so zone geometry behind land/peninsulas is hidden. depthWrite
    // stays off so these transparent layers don't occlude each other.
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  };
}

export function createZoneMaterial(opacity: number, shared: SharedUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-zone',
    vertexShader: ZONE_VERT,
    fragmentShader: ZONE_FRAG,
    uniforms: {
      uColor: { value: new THREE.Color(RENDER_STYLE.zoneColor) },
      uOpacity: { value: opacity },
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
    },
  });
}

export function createFinishMaterial(shared: SharedUniforms): THREE.ShaderMaterial {
  const m = new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-finish',
    vertexShader: FINISH_VERT,
    fragmentShader: FINISH_FRAG,
    uniforms: {
      uWhite: { value: new THREE.Color(RENDER_STYLE.finishWhite) },
      uBlack: { value: new THREE.Color(RENDER_STYLE.finishBlack) },
      uOpacity: { value: RENDER_STYLE.finishOpacity },
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
      uHeight: {
        value: new THREE.Vector3(
          Math.tan(THREE.MathUtils.degToRad(RENDER_STYLE.finishAngularHeightDeg)),
          RENDER_STYLE.finishMinHeight,
          RENDER_STYLE.finishMaxHeight,
        ),
      },
    },
  });
  return m;
}

/**
 * Zone-boundary wall: a vertical fence of fixed height standing on the water (NOT
 * distance-scaled, unlike the finish band -- height is baked directly into the geometry's y).
 * Vertical gradient (opaque at the bottom, fading towards the top) plus a thin brighter top
 * edge strip so it reads as a fence, on top of the same distance fade as the zone fills.
 */
const WALL_VERT = /* glsl */ `
uniform float uWallHeight;
varying vec3 vViewPos;
varying float vFrac;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vViewPos = mv.xyz;
  vFrac = uWallHeight > 0.0 ? position.y / uWallHeight : 0.0;
  gl_Position = projectionMatrix * mv;
}
`;

const WALL_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uEdgeColor;
uniform float uOpacity;
uniform float uGlobal;
uniform vec2 uFade;
uniform float uTopAlpha;
uniform float uEdgeFrac;
uniform float uEdgeOpacity;
varying vec3 vViewPos;
varying float vFrac;
void main() {
  float f = clamp(vFrac, 0.0, 1.0);
  // Bright white top edge strip (flat opacity, independent of the red body's opacity) so the
  // wall reads as a fence; the body fades from opaque at the bottom to uTopAlpha at the top.
  float edgeMask = step(1.0 - uEdgeFrac, f);
  vec3 color = mix(uColor, uEdgeColor, edgeMask);
  float bodyAlpha = uOpacity * mix(1.0, uTopAlpha, f);
  float alpha = mix(bodyAlpha, uEdgeOpacity, edgeMask);
  float fade = 1.0 - smoothstep(uFade.x, uFade.y, length(vViewPos));
  gl_FragColor = vec4(color, uGlobal * fade * alpha);
  #include <colorspace_fragment>
}
`;

/**
 * Pier lines (thin outlines of piers/jetties/notches on the water). Reuses the zone
 * shader/fade unchanged (it only depends on view-space distance): white, at fixed opacity.
 */
export function createPierLineMaterial(shared: SharedUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-pier',
    vertexShader: ZONE_VERT,
    fragmentShader: ZONE_FRAG,
    uniforms: {
      uColor: { value: new THREE.Color(RENDER_STYLE.pierColor) },
      uOpacity: { value: RENDER_STYLE.pierOpacity },
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
    },
  });
}

export function createWallMaterial(opacity: number, shared: SharedUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-wall',
    vertexShader: WALL_VERT,
    fragmentShader: WALL_FRAG,
    uniforms: {
      uColor: { value: new THREE.Color(RENDER_STYLE.wallColor) },
      uEdgeColor: { value: new THREE.Color(RENDER_STYLE.wallEdgeColor) },
      uOpacity: { value: opacity },
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
      uWallHeight: { value: RENDER_STYLE.wallHeight },
      uTopAlpha: { value: RENDER_STYLE.wallTopAlpha },
      uEdgeFrac: { value: RENDER_STYLE.wallEdgeHeight / RENDER_STYLE.wallHeight },
      uEdgeOpacity: { value: RENDER_STYLE.wallEdgeOpacity },
    },
  });
}

/**
 * Wall base line: a bright rail lying on the water along every wall segment (LineSegments).
 * Reuses the zone shader/fade unchanged: flat colour + opacity, same distance fade as everything
 * else.
 */
export function createWallBaseMaterial(shared: SharedUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-wall-base',
    vertexShader: ZONE_VERT,
    fragmentShader: ZONE_FRAG,
    uniforms: {
      uColor: { value: new THREE.Color(RENDER_STYLE.wallBaseColor) },
      uOpacity: { value: RENDER_STYLE.wallBaseOpacity },
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
    },
  });
}

/**
 * Shore occluder: invisible vertical quads along the lakeOutline rings. colorWrite is off, so
 * this only writes depth -- painted first (RENDER_ORDER.occluder), it hides zone geometry
 * behind land/peninsulas from the camera without drawing anything itself. Top vertices
 * (aSide === 1) are raised to uOccHeight, scaled by smoothstep(uOccNear.x, uOccNear.y, distance
 * from the vertex to the user in the XZ plane) so GPS error near the shore doesn't hide
 * everything.
 */
const OCCLUDER_VERT = /* glsl */ `
attribute float aSide;
uniform vec2 uUser;
uniform float uOccHeight;
uniform vec2 uOccNear;
void main() {
  float dist = distance(position.xz, uUser);
  float factor = smoothstep(uOccNear.x, uOccNear.y, dist);
  float y = aSide * uOccHeight * factor;
  vec4 mv = modelViewMatrix * vec4(position.x, y, position.z, 1.0);
  gl_Position = projectionMatrix * mv;
}
`;

const OCCLUDER_FRAG = /* glsl */ `
void main() {
  gl_FragColor = vec4(0.0);
}
`;

export function createShoreOccluderMaterial(shared: SharedUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: 'uferzone-shore-occluder',
    vertexShader: OCCLUDER_VERT,
    fragmentShader: OCCLUDER_FRAG,
    side: THREE.DoubleSide,
    transparent: false,
    depthTest: true,
    depthWrite: true,
    colorWrite: false,
    uniforms: {
      uUser: shared.uUser,
      uOccHeight: { value: RENDER_STYLE.shoreOccluderHeight },
      uOccNear: { value: new THREE.Vector2(RENDER_STYLE.shoreOccluderNearM, RENDER_STYLE.shoreOccluderFarM) },
    },
  });
}

export function createSlipwayMaterial(shared: SharedUniforms, iconTexture: THREE.Texture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    ...baseParams(),
    name: 'uferzone-slipway',
    vertexShader: SLIPWAY_VERT,
    fragmentShader: SLIPWAY_FRAG,
    uniforms: {
      uGlobal: shared.uGlobal,
      uFade: shared.uFade,
      uIcon: { value: iconTexture },
      uHeight: {
        value: new THREE.Vector3(
          Math.tan(THREE.MathUtils.degToRad(RENDER_STYLE.slipwayAngularHeightDeg)),
          RENDER_STYLE.slipwayMinHeight,
          RENDER_STYLE.slipwayMaxHeight,
        ),
      },
    },
  });
}

/**
 * Builds the slipway sign texture once per ZoneScene: a square OSM-carto leisure=slipway icon
 * (white) on a blue (@transportation-icon) background with a white border, matching the official
 * carto style. Falls back to a plain blue square if the icon isn't bundled or `document` isn't
 * available (Node/tests), per the caller's dispose contract (the returned texture should be
 * disposed once, in ZoneScene.dispose()).
 */
export function createSlipwaySignTexture(): THREE.Texture {
  const size = 128;
  const icon = osmIcon('leisure=slipway');
  const canDraw = icon && typeof document !== 'undefined';

  if (!canDraw) {
    // Plain blue square (1x1), scaled by the GPU sampler — used in Node/tests or if the icon is
    // missing.
    const c = new THREE.Color(OSM_TRANSPORT_ICON_COLOR);
    const data = new Uint8Array([Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255), 255]);
    const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  // White border (~8% of the side), blue fill, white icon centred (~70% of the side).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const border = size * 0.08;
  ctx.fillStyle = OSM_TRANSPORT_ICON_COLOR;
  ctx.fillRect(border, border, size - 2 * border, size - 2 * border);
  ctx.fillStyle = '#ffffff';
  drawOsmIcon(ctx, icon!, size / 2, size / 2, size * 0.7);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
